/**
 * O que a tela mostra sobre o tempo real.
 *
 * - `offline`: nenhum socket conectado — é o único caso em que "reconectando" é verdade.
 * - `syncing`: conectado, porém sem receber nada; a tela está sendo servida pelo poller REST.
 * - `none`: nada a dizer.
 */
export type RealtimeNoticeLevel = 'none' | 'syncing' | 'offline'

/** Estado do mundo, como o componente o observa. */
export type RealtimeNoticeInput = 'ok' | 'syncing' | 'offline'

export type RealtimeStatusNoticeOptions = {
	/** Chamado quando o nível VISÍVEL muda — sirva um `markForCheck()` aqui. */
	onChange: (level: RealtimeNoticeLevel) => void
	/** Espera antes de anunciar: absorve o flap curto, que o usuário não tem como agir sobre. */
	appearDelayMs?: number
	/** Tempo de normalidade exigido para retirar o aviso. */
	stableForMs?: number
	/** Já anunciado, fica no ar ao menos isto — evita o flash de meio segundo. */
	minVisibleMs?: number
	/** Injetável para teste. */
	now?: () => number
}

const DEFAULT_APPEAR_DELAY_MS = 2_000
const DEFAULT_STABLE_FOR_MS = 3_000
const DEFAULT_MIN_VISIBLE_MS = 4_000

/**
 * Aviso de tempo real com HISTERESE.
 *
 * O desenho anterior atrasava a entrada em 2s e fazia a saída na hora. Parece conservador, mas é o
 * contrário: num laço de reconexão (conecta, falha, cai, tenta de novo) cada `connected` apagava o
 * aviso instantaneamente e cada queda o trazia de volta — o rótulo piscava sem parar, que foi o
 * relato de 23/09/2026 ao ligar o relay STOMP. Piscar é pior que informar: o usuário não consegue
 * ler, e a tela passa a sensação de estar quebrada.
 *
 * Aqui a assimetria é deliberada e no sentido útil: **entra devagar e sai mais devagar ainda**. Para
 * o aviso desaparecer, a conexão tem de ficar boa por [stableForMs] — no laço ela nunca fica, então o
 * aviso permanece estável, dizendo a verdade, em vez de tremer. E [minVisibleMs] garante que um aviso
 * já mostrado não desapareça antes de ser legível.
 *
 * Piorar tem passagem livre: `syncing` → `offline` troca o texto na hora, sem esperar, porque é
 * informação nova e relevante. O caminho de volta é que é cauteloso.
 */
export class RealtimeStatusNotice {
	private readonly appearDelayMs: number
	private readonly stableForMs: number
	private readonly minVisibleMs: number
	private readonly now: () => number

	private visible: RealtimeNoticeLevel = 'none'
	private shownAt: number | null = null
	private appearHandle?: ReturnType<typeof setTimeout>
	private hideHandle?: ReturnType<typeof setTimeout>
	private pending: RealtimeNoticeLevel = 'none'

	constructor(private readonly options: RealtimeStatusNoticeOptions) {
		this.appearDelayMs = options.appearDelayMs ?? DEFAULT_APPEAR_DELAY_MS
		this.stableForMs = options.stableForMs ?? DEFAULT_STABLE_FOR_MS
		this.minVisibleMs = options.minVisibleMs ?? DEFAULT_MIN_VISIBLE_MS
		this.now = options.now ?? (() => Date.now())
	}

	/** O nível que a tela deve exibir agora. */
	get level(): RealtimeNoticeLevel {
		return this.visible
	}

	get offline(): boolean {
		return this.visible === 'offline'
	}

	update(input: RealtimeNoticeInput): void {
		if (input === 'ok') {
			this.pending = 'none'
			this.scheduleHide()
			return
		}

		this.pending = input
		this.clearHide()

		if (this.visible !== 'none') {
			// Já visível: só a piora troca o texto de imediato. `offline` -> `syncing` espera a
			// confirmação de normalidade, senão volta a piscar por outro caminho.
			if (input === 'offline' && this.visible !== 'offline') this.show('offline')
			return
		}

		if (this.appearHandle) return

		this.appearHandle = setTimeout(() => {
			this.appearHandle = undefined
			if (this.pending === 'none') return
			this.show(this.pending)
		}, this.appearDelayMs)
	}

	/** Recolhe o aviso sem carência — para quando a tela sai do ar (troca de sala, destroy). */
	reset(): void {
		this.clearAppear()
		this.clearHide()
		this.pending = 'none'
		this.shownAt = null

		if (this.visible !== 'none') {
			this.visible = 'none'
			this.options.onChange(this.visible)
		}
	}

	dispose(): void {
		this.clearAppear()
		this.clearHide()
	}

	private show(level: RealtimeNoticeLevel): void {
		this.clearAppear()
		this.visible = level
		this.shownAt = this.now()
		this.options.onChange(this.visible)
	}

	private scheduleHide(): void {
		this.clearAppear()

		if (this.visible === 'none' || this.hideHandle) return

		const visibleFor = this.shownAt === null ? this.minVisibleMs : this.now() - this.shownAt
		const restanteMinimo = Math.max(0, this.minVisibleMs - visibleFor)

		this.hideHandle = setTimeout(
			() => {
				this.hideHandle = undefined
				if (this.pending !== 'none') return

				this.visible = 'none'
				this.shownAt = null
				this.options.onChange(this.visible)
			},
			Math.max(this.stableForMs, restanteMinimo)
		)
	}

	private clearAppear(): void {
		if (!this.appearHandle) return

		clearTimeout(this.appearHandle)
		this.appearHandle = undefined
	}

	private clearHide(): void {
		if (!this.hideHandle) return

		clearTimeout(this.hideHandle)
		this.hideHandle = undefined
	}
}
